import {
  flexRender,
  type Header,
  type SortingState,
  type Table,
  type VisibilityState,
} from '@tanstack/react-table';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  CheckboxOption,
  FilterPopover,
} from '@/routes/admin/operational-view/operational-view-filter-menu';

// =============================================================================
// operational-view-table.tsx — Helpers PARTAGÉS des 4 tables des pages Profils & Fournisseurs (Cx-6, §2.5 v2.2).
// =============================================================================
// Tri + masquage de colonnes + rendu `ds-table` commun. a11y : en-tête triable = <th aria-sort> contenant un
// <button> ; l'indicateur de sens (▲/▼) est décoratif (aria-hidden), le sens est porté par `aria-sort`.
// Masquage = cases <label htmlFor> dans le menu « Colonnes ▾ ». Aucune règle métier ici.
//
// Restyling 2026-09-17 (maquette, GATE_HUMAN « simple restyling ») : gouttière 24 px (pl-6 / pr-6 aux bords),
// valeurs numériques alignées à droite en tabular-nums, ligne sélectionnée = bande `dash-band` + filet gauche
// 3 px (parité registre des campagnes), voile Hors Jeux `row-out-of-play` conservé.

/** Convertit les préférences (colonnes masquées + tri) en états TanStack (visibility + sorting). */
export function toTableState(
  hiddenColumns: readonly string[],
  sort: { id: string; desc: boolean } | null,
): { visibility: VisibilityState; sorting: SortingState } {
  const visibility: VisibilityState = {};
  for (const id of hiddenColumns) {
    visibility[id] = false;
  }
  return { visibility, sorting: sort === null ? [] : [sort] };
}

/** Filet gauche 3 px de la ligne sélectionnée (parité registre des campagnes / tableau de bord). */
const SELECTED_RAIL = 'shadow-[inset_3px_0_0_0_hsl(var(--foreground))]';

/** Gouttière 24 px sur la 1re et la dernière cellule (homogénéité Panel). */
function edgeClass(index: number, count: number): string {
  return cn(index === 0 && 'pl-6', index === count - 1 && 'pr-6');
}

/** En-tête de colonne triable : `<th aria-sort>` + `<button>` (ou simple `<th>` si non triable). */
export function SortableTh<TData>({
  header,
  numeric = false,
  className,
}: {
  readonly header: Header<TData, unknown>;
  /** Colonne de valeurs chiffrées : en-tête aligné à droite. */
  readonly numeric?: boolean;
  readonly className?: string;
}): React.JSX.Element {
  const sorted = header.column.getIsSorted(); // 'asc' | 'desc' | false
  const canSort = header.column.getCanSort();
  const ariaSort: React.AriaAttributes['aria-sort'] = !canSort
    ? undefined
    : sorted === 'asc'
      ? 'ascending'
      : sorted === 'desc'
        ? 'descending'
        : 'none';
  const content = header.isPlaceholder
    ? null
    : flexRender(header.column.columnDef.header, header.getContext());

  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={cn(
        'whitespace-nowrap px-4 py-3',
        numeric ? 'text-right' : 'text-left',
        className,
      )}
    >
      {canSort && !header.isPlaceholder ? (
        <button
          type="button"
          onClick={header.column.getToggleSortingHandler()}
          className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {content}
          <span aria-hidden="true">
            {sorted === 'asc' ? '▲' : sorted === 'desc' ? '▼' : '↕'}
          </span>
        </button>
      ) : (
        content
      )}
    </th>
  );
}

/**
 * Menu « Colonnes n / m ▾ » : une case par colonne masquable (TanStack columnVisibility). Mêmes cases et mêmes
 * identifiants que l'ancien bloc « Colonnes affichées » (la persistance reste portée par l'appelant).
 */
export function ColumnsMenu<TData>({
  table,
  idPrefix,
  withCount = true,
}: {
  readonly table: Table<TData>;
  /** Préfixe d'id unique par table (évite la collision d'id entre 2 tables d'une même page). */
  readonly idPrefix: string;
  /** Affiche le compteur « n / m » dans le déclencheur (matrice, liste) ; « Colonnes » seul sinon (détail). */
  readonly withCount?: boolean;
}): React.JSX.Element {
  const hideable = table.getAllLeafColumns().filter((c) => c.getCanHide());
  const visibleCount = hideable.filter((c) => c.getIsVisible()).length;
  const triggerLabel = withCount
    ? `Colonnes ${String(visibleCount)} / ${String(hideable.length)}`
    : 'Colonnes';
  return (
    <FilterPopover
      triggerLabel={triggerLabel}
      groupLabel="Colonnes affichées"
      align="end"
    >
      {hideable.map((col) => (
        <CheckboxOption
          key={col.id}
          id={`${idPrefix}-col-${col.id}`}
          label={String(col.columnDef.header)}
          checked={col.getIsVisible()}
          onChange={col.getToggleVisibilityHandler()}
        />
      ))}
    </FilterPopover>
  );
}

/** Badge « Non actualisé » (cycle non actualisé) : LIBELLÉ TEXTE, la teinte ambre n'est qu'un renfort. */
export function StaleCycleBadge(): React.JSX.Element {
  return (
    <Badge
      variant="outline"
      className="whitespace-nowrap border-transparent bg-dash-critical-bg text-dash-critical-fg"
    >
      Non actualisé
    </Badge>
  );
}

/**
 * Badge « Hors Jeux » du détail : texte teinté métier pourpre (contraste AA prouvé sur fond de carte ET sur le
 * voile row-out-of-play, cf. price-code-badge) ; le libellé porte l'information, le point est décoratif.
 */
export function HorsJeuBadge(): React.JSX.Element {
  return (
    <Badge
      variant="outline"
      className="gap-1 whitespace-nowrap border-metier-pourpre/30 text-metier-pourpre"
    >
      <span aria-hidden="true" className="size-1.5 rounded-full bg-metier-pourpre" />
      {/* Libellé ALIGNÉ sur le reste de l'application (price-code-badge « Hors Jeux ») — arbitrage John
          2026-09-17 : « OK pour l'alignement ». */}
      Hors Jeux
    </Badge>
  );
}

/** Tiret muet d'une cellule sans valeur (cycle à jour, pas de Hors Jeux). */
export function EmptyCell(): React.JSX.Element {
  return <span className="text-muted-foreground">—</span>;
}

export interface OperationalTableProps<TData> {
  readonly table: Table<TData>;
  /** Légende (sr-only) : nomme la table pour les lecteurs d'écran et les tests. */
  readonly caption: string;
  /** Ids des colonnes chiffrées (alignées à droite). */
  readonly numericColumnIds: ReadonlySet<string>;
  /** Classes additionnelles de l'en-tête de ligne (ex. mono pour un SIRET). */
  readonly rowHeaderClassName?: string;
  /** Sélection de ligne (matrice, liste) : 1re cellule = bouton clavier, ligne cliquable à la souris. */
  readonly selection?: {
    readonly isSelected: (row: TData) => boolean;
    readonly onSelect: (row: TData, trigger: HTMLButtonElement | null) => void;
  };
  /** Voile Hors Jeux (renfort visuel du badge texte). */
  readonly isOutOfPlay?: (row: TData) => boolean;
}

/** Table `ds-table` commune (en-têtes triables, en-tête de ligne, sélection, voile Hors Jeux). */
export function OperationalTable<TData>({
  table,
  caption,
  numericColumnIds,
  rowHeaderClassName,
  selection,
  isOutOfPlay,
}: OperationalTableProps<TData>): React.JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="ds-table w-full border-collapse text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} className="border-b border-border bg-muted/40">
              {hg.headers.map((header, index) => (
                <SortableTh
                  key={header.id}
                  header={header}
                  numeric={numericColumnIds.has(header.column.id)}
                  className={edgeClass(index, hg.headers.length)}
                />
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const isSelected = selection?.isSelected(row.original) === true;
            const outOfPlay = isOutOfPlay?.(row.original) === true;
            const cells = row.getVisibleCells();
            return (
              <tr
                key={row.id}
                aria-selected={selection === undefined ? undefined : isSelected}
                onClick={
                  selection === undefined
                    ? undefined
                    : (event) =>
                        selection.onSelect(
                          row.original,
                          event.currentTarget.querySelector('button'),
                        )
                }
                className={cn(
                  'border-b border-border last:border-b-0',
                  selection !== undefined && 'cursor-pointer',
                  isSelected && 'bg-dash-band hover:bg-dash-band',
                  // Voile de fond SOLIDE (Cx-S19-2 = OPTION B) : le texte garde sa pleine opacité (contraste
                  // >= 4.5:1). Le LIBELLÉ « Hors Jeux » de la colonne « Hors jeu » porte l'information (WCAG 1.4.1).
                  outOfPlay && 'bg-row-out-of-play',
                )}
              >
                {cells.map((cell, index) => {
                  const rendered = flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext(),
                  );
                  const numeric = numericColumnIds.has(cell.column.id);
                  if (index === 0) {
                    return (
                      <th
                        key={cell.id}
                        scope="row"
                        className={cn(
                          'py-3 pl-6 pr-4 text-left align-middle font-medium',
                          isSelected && SELECTED_RAIL,
                          rowHeaderClassName,
                        )}
                      >
                        {selection === undefined ? (
                          rendered
                        ) : (
                          <button
                            type="button"
                            className="text-left underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            onClick={(event) => {
                              event.stopPropagation();
                              selection.onSelect(row.original, event.currentTarget);
                            }}
                          >
                            {rendered}
                          </button>
                        )}
                      </th>
                    );
                  }
                  return (
                    <td
                      key={cell.id}
                      className={cn(
                        'px-4 py-3 align-middle tabular-nums',
                        numeric && 'whitespace-nowrap text-right',
                        index === cells.length - 1 && 'pr-6',
                      )}
                    >
                      {rendered}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Pied de card : texte descriptif à gauche, action optionnelle à droite (bordure haute, texte muted petit). */
export function PanelFooter({
  children,
  action,
}: {
  readonly children: React.ReactNode;
  readonly action?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t border-border px-6 py-3 text-xs text-muted-foreground">
      <p>{children}</p>
      {action}
    </div>
  );
}

/** Barre de filtres sous la barre de titre d'une card (champ, menus, case, colonnes à droite). */
export function PanelToolbar({
  children,
}: {
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-border px-6 py-3">
      {children}
    </div>
  );
}

/** Message d'état (chargement, erreur, vide) dans le corps d'une card, gouttière 24 px. */
export function PanelMessage({
  kind,
  children,
}: {
  readonly kind: 'status' | 'alert';
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <p
      role={kind}
      className={cn(
        'px-6 py-4 text-sm',
        kind === 'alert' ? 'text-destructive' : 'text-muted-foreground',
      )}
    >
      {children}
    </p>
  );
}
